import React, { useState } from 'react';

/* ---------- Design tokens ---------- */
const c = {
  cardBg: '#F8F5FF',
  badgeBg: '#E8DDFF',
  badgePurple: '#7209B7',
  titleColor: '#1A1824',
  descColor: '#5B586E',
  bulletColor: '#4A4859',
  checkGreen: '#10B981',
};

/* ---------- Reusable small building blocks ---------- */
const CheckIcon = () => (
  <span
    style={{ backgroundColor: c.checkGreen }}
    className="w-4 h-4 rounded-full flex items-center justify-center shrink-0 mt-[1px]"
  >
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
  </span>
);

const BulletIcon = () => (
  <span style={{ backgroundColor: '#A19FB1' }} className="w-1 h-1 rounded-full mt-[7px] shrink-0" />
);

/*
  Preview-safe image component
*/
const CardImg = ({ src, alt, className }) => {
  const [broken, setBroken] = useState(false);
  if (broken) {
    return <div className={`${className} bg-[#E8DDFF]/60 rounded-xl`} />;
  }
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setBroken(true)}
    />
  );
};

/* ---------- Main Component ---------- */

const CommunityGuidelinesGrid = () => {
  return (
    <div style={{ fontFamily: "'Poppins', sans-serif" }} className="w-full overflow-x-hidden bg-white py-12 px-4 sm:px-8 lg:px-16 font-['Poppins']">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap');
        .no-scrollbar { scrollbar-width: none; -ms-overflow-style: none; -webkit-overflow-scrolling: touch; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
      `}</style>

      {/* Header */}
      <div className="max-w-4xl mx-auto text-center mb-12">
        <h1 style={{ color: c.titleColor }} className="text-3xl sm:text-4xl md:text-[40px] font-extrabold tracking-tight font-['Poppins']">
          Our Community <span style={{ color: c.badgePurple }}>Guidelines</span>
        </h1>
        <p style={{ color: c.descColor }} className="text-base sm:text-lg mt-4 max-w-2xl mx-auto font-['Poppins']">
          Please follow these guidelines to help us build a safe, respectful, and trustworthy travel community.
        </p>
      </div>

      {/* Card row */}
      <div className="max-w-7xl mx-auto flex overflow-x-auto snap-x snap-mandatory gap-4 pb-2 no-scrollbar -mx-4 px-4 sm:-mx-8 sm:px-8 lg:mx-0 lg:px-0 md:grid md:overflow-visible md:snap-none md:mx-0 md:px-0 md:grid-cols-2 xl:grid-cols-4 md:gap-6">

        {/* CARD 01: Be Respectful */}
        <div className="relative shrink-0 w-[82%] xs:w-[78%] snap-center min-w-0 md:w-auto md:shrink rounded-3xl h-[450px] bg-[#F6F1FE]">
          <div className="relative w-full h-44 sm:h-48 pt-6 px-4 flex justify-center items-end bg-transparent">
            <CardImg src="/img/community-guidelines/BeRespectful.svg" alt="Be Respectful" className="max-h-full max-w-full object-contain" />
          </div>
          <div className="p-6 pt-4 flex flex-col flex-grow">
            <h3 style={{ color: c.titleColor }} className="font-bold text-sm sm:text-base mb-2 tracking-tight font-['Poppins']">Be Respectful</h3>
            <p style={{ color: c.descColor }} className="text-[13px] leading-relaxed mb-4 font-['Poppins']">
              Treat every member with kindness, courtesy, and respect. Everyone deserves to feel welcome.
            </p>
            <ul className="flex flex-col mt-auto gap-2.5 font-['Poppins']">
              <li className="flex items-center gap-2.5">
                <CheckIcon />
                <span style={{ color: c.titleColor }} className="text-[13px] font-medium leading-tight">Kind Conversations</span>
              </li>
              <li className="flex items-center gap-2.5">
                <CheckIcon />
                <span style={{ color: c.titleColor }} className="text-[13px] font-medium leading-tight">Respect Boundaries</span>
              </li>
              <li className="flex items-center gap-2.5">
                <CheckIcon />
                <span style={{ color: c.titleColor }} className="text-[13px] font-medium leading-tight">Celebrate Diversity</span>
              </li>
            </ul>
          </div>
        </div>

        {/* CARD 02: Be Honest */}
        <div className="relative shrink-0 w-[82%] xs:w-[78%] snap-center min-w-0 md:w-auto md:shrink rounded-3xl h-[450px] bg-[#F6F1FE]">
          <div className="relative w-full h-44 sm:h-48 pt-6 px-4 flex justify-center items-end bg-transparent">
            <CardImg src="/img/community-guidelines/BeHonest.svg" alt="Be Honest" className="max-h-full max-w-full object-contain" />
          </div>
          <div className="p-6 pt-4 flex flex-col flex-grow">
            <h3 style={{ color: c.titleColor }} className="font-bold text-sm sm:text-base mb-2 tracking-tight font-['Poppins']">Be Honest</h3>
            <p style={{ color: c.descColor }} className="text-[13px] leading-relaxed mb-4 font-['Poppins']">
              Create genuine profiles and share accurate travel information.
            </p>
            <ul className="flex flex-col mt-auto gap-2.5 font-['Poppins']">
              <li className="flex items-center gap-2.5">
                <CheckIcon />
                <span style={{ color: c.titleColor }} className="text-[13px] font-medium leading-tight">No Fake Profiles</span>
              </li>
              <li className="flex items-center gap-2.5">
                <CheckIcon />
                <span style={{ color: c.titleColor }} className="text-[13px] font-medium leading-tight">No Misleading Information</span>
              </li>
              <li className="flex items-center gap-2.5">
                <CheckIcon />
                <span style={{ color: c.titleColor }} className="text-[13px] font-medium leading-tight">Authentic Travel Plans</span>
              </li>
            </ul>
          </div>
        </div>

        {/* CARD 03: Communicate Responsibly */}
        <div className="relative shrink-0 w-[82%] xs:w-[78%] snap-center min-w-0 md:w-auto md:shrink rounded-3xl h-[450px] bg-[#F0E9FB]">
          <div className="relative w-full h-44 sm:h-48 pt-6 px-4 flex justify-center items-end bg-transparent">
            <CardImg src="/img/community-guidelines/CommunicateResponsibly.svg" alt="Communicate Responsibly" className="max-h-full max-w-full object-contain" />
          </div>
          <div className="p-6 pt-4 flex flex-col flex-grow">
            <h3 style={{ color: c.titleColor }} className="font-bold text-sm sm:text-base mb-2 whitespace-nowrap tracking-tight font-['Poppins']">
              Communicate Responsibly
            </h3>
            <p style={{ color: c.descColor }} className="text-[13px] leading-relaxed mb-4 font-['Poppins']">
              Use SoloConnect to connect respectfully and appropriately with fellow travelers.
            </p>
            <ul className="flex flex-col mt-auto gap-2.5 font-['Poppins']">
              <li className="flex items-center gap-2.5">
                <CheckIcon />
                <span style={{ color: c.titleColor }} className="text-[13px] font-medium leading-tight">Be Polite & Respectful</span>
              </li>
              <li className="flex items-center gap-2.5">
                <CheckIcon />
                <span style={{ color: c.titleColor }} className="text-[13px] font-medium leading-tight">Avoid Inappropriate Language</span>
              </li>
              <li className="flex items-center gap-2.5">
                <CheckIcon />
                <span style={{ color: c.titleColor }} className="text-[13px] font-medium leading-tight">Respect Personal Space</span>
              </li>
            </ul>
          </div>
        </div>

        {/* CARD 04: Content Not Allowed */}
        <div className="relative shrink-0 w-[82%] xs:w-[78%] snap-center min-w-0 md:w-auto md:shrink rounded-3xl h-[450px] bg-[#F4EEFD]">
          <div className="relative w-full h-44 sm:h-48 pt-6 px-4 flex justify-center items-end bg-transparent">
            <CardImg src="/img/community-guidelines/ContentNotAllowed.svg" alt="Content Not Allowed" className="max-h-full max-w-full object-contain" />
          </div>
          <div className="p-6 pt-2 flex flex-col flex-grow font-['Poppins']">
            <h3 style={{ color: c.titleColor }} className="font-bold text-sm sm:text-base mb-1 tracking-tight font-['Poppins']">Content Not Allowed</h3>
            <ul className="flex flex-col mt-2 gap-1.5">
              <li className="flex items-start gap-2"><BulletIcon /><span style={{ color: c.descColor }} className="text-[11px] leading-snug">Harassment or bullying</span></li>
              <li className="flex items-start gap-2"><BulletIcon /><span style={{ color: c.descColor }} className="text-[11px] leading-snug">Hate speech or discriminatory content</span></li>
              <li className="flex items-start gap-2"><BulletIcon /><span style={{ color: c.descColor }} className="text-[11px] leading-snug">Threats or intimidation</span></li>
              <li className="flex items-start gap-2"><BulletIcon /><span style={{ color: c.descColor }} className="text-[11px] leading-snug">Sexually explicit or adult content</span></li>
              <li className="flex items-start gap-2"><BulletIcon /><span style={{ color: c.descColor }} className="text-[11px] leading-snug">Nudity or obscene material</span></li>
              <li className="flex items-start gap-2"><BulletIcon /><span style={{ color: c.descColor }} className="text-[11px] leading-snug">Spam or repetitive promotional content</span></li>
              <li className="flex items-start gap-2"><BulletIcon /><span style={{ color: c.descColor }} className="text-[11px] leading-snug">Fraud, scams, or phishing attempts</span></li>
              <li className="flex items-start gap-2"><BulletIcon /><span style={{ color: c.descColor }} className="text-[11px] leading-snug">Illegal activities</span></li>
            </ul>
          </div>
        </div>

        {/* CARD 05: Prohibited Activities */}
        <div className="relative shrink-0 w-[82%] xs:w-[78%] snap-center min-w-0 md:w-auto md:shrink rounded-3xl h-[430px] bg-[#F6F1FE]">
          <div className="relative w-full h-44 sm:h-48 pt-6 px-4 flex justify-center items-end bg-transparent">
            <CardImg src="/img/community-guidelines/ProhibitedActivities.svg" alt="Prohibited Activities" className="max-h-full max-w-full object-contain" />
          </div>
          <div className="p-6 pt-4 flex flex-col flex-grow font-['Poppins']">
            <h3 style={{ color: c.titleColor }} className="font-bold text-sm sm:text-base mb-2 tracking-tight font-['Poppins']">Prohibited Activities</h3>
            <p style={{ color: c.descColor }} className="text-[13px] leading-relaxed mb-4">
              Do not create fake accounts, impersonate others, use bots, or misuse personal information.
            </p>
            <ul className="flex flex-col mt-auto gap-2.5">
              <li className="flex items-center gap-2.5">
                <CheckIcon />
                <span style={{ color: c.titleColor }} className="text-[13px] font-medium leading-tight">No Impersonation</span>
              </li>
              <li className="flex items-center gap-2.5">
                <CheckIcon />
                <span style={{ color: c.titleColor }} className="text-[13px] font-medium leading-tight">No Spam or Ads</span>
              </li>
              <li className="flex items-center gap-2.5">
                <CheckIcon />
                <span style={{ color: c.titleColor }} className="text-[13px] font-medium leading-tight">No Hacking or Disruption</span>
              </li>
            </ul>
          </div>
        </div>

        {/* CARD 06: Travel Safely */}
        <div className="relative shrink-0 w-[82%] xs:w-[78%] snap-center min-w-0 md:w-auto md:shrink rounded-3xl h-[430px] bg-[#F6F1FE]">
          <div className="relative w-full h-44 sm:h-48 pt-6 px-4 flex justify-center items-end bg-transparent">
            <CardImg src="/img/community-guidelines/TravelSafelyCard.svg" alt="Travel Safely" className="max-h-full max-w-full object-contain" />
          </div>
          <div className="p-6 pt-4 flex flex-col flex-grow font-['Poppins']">
            <h3 style={{ color: c.titleColor }} className="font-bold text-sm sm:text-base mb-2 tracking-tight font-['Poppins']">Travel Safely</h3>
            <p style={{ color: c.descColor }} className="text-[13px] leading-relaxed mb-4">
              Your safety is your responsibility. Follow smart travel practices at all times.
            </p>
            <ul className="flex flex-col mt-auto gap-2.5">
              <li className="flex items-center gap-2.5">
                <CheckIcon />
                <span style={{ color: c.titleColor }} className="text-[13px] font-medium leading-tight">Meet in Public Places</span>
              </li>
              <li className="flex items-center gap-2.5">
                <CheckIcon />
                <span style={{ color: c.titleColor }} className="text-[13px] font-medium leading-tight">Inform Family or Friends</span>
              </li>
              <li className="flex items-center gap-2.5">
                <CheckIcon />
                <span style={{ color: c.titleColor }} className="text-[13px] font-medium leading-tight">Trust Your Instincts</span>
              </li>
            </ul>
          </div>
        </div>

        {/* CARD 07: Use Safety Features */}
        <div className="relative shrink-0 w-[82%] xs:w-[78%] snap-center min-w-0 md:w-auto md:shrink rounded-3xl h-[430px] bg-[#F6F1FE]">
          <div className="relative w-full h-44 sm:h-48 pt-6 px-4 flex justify-center items-end bg-transparent">
            <CardImg src="/img/community-guidelines/UseSafetyFeatures.svg" alt="Use Safety Features" className="max-h-full max-w-full object-contain" />
          </div>
          <div className="p-6 pt-4 flex flex-col flex-grow font-['Poppins']">
            <h3 style={{ color: c.titleColor }} className="font-bold text-sm sm:text-base mb-2 tracking-tight font-['Poppins']">Use Safety Features</h3>
            <p style={{ color: c.descColor }} className="text-[13px] leading-relaxed mb-4">
              Use our safety tools to enhance your travel experience.
            </p>
            <div className="flex justify-between items-start mt-auto pt-4 gap-2">
              <div className="flex flex-col items-center text-center">
                <div style={{ backgroundColor: '#E2FBE8' }} className="w-8 h-8 rounded-full flex items-center justify-center mb-1.5 shadow-sm">
                  <CardImg src="/img/community-guidelines/safety-badge1.svg" alt="Verify" className="w-5 h-5" />
                </div>
                <span style={{ color: c.titleColor }} className="text-[10px] leading-tight font-semibold whitespace-pre-line">Verify</span>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="w-8 h-8 rounded-full flex items-center justify-center mb-1.5 shadow-sm">
                  <CardImg src="/img/community-guidelines/safety-badge2.svg" alt="Verify" className="w-8 h-8" />
                </div>
                <span style={{ color: c.titleColor }} className="text-[10px] leading-tight font-semibold whitespace-pre-line">Safe Mode</span>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="w-8 h-8 rounded-full flex items-center justify-center mb-1.5 shadow-sm">
                  <CardImg src="/img/community-guidelines/safety-badge3.svg" alt="Verify" className="w-8 h-8" />
                </div>
                <span style={{ color: c.titleColor }} className="text-[10px] leading-tight font-semibold whitespace-pre-line">{"Trusted\nContacts"}</span>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="w-8 h-8 rounded-full flex items-center justify-center mb-1.5">
                  <CardImg src="/img/community-guidelines/safety-badge4.svg" alt="Verify" className="w-8 h-8" />
                </div>
                <span style={{ color: c.titleColor }} className="text-[10px] leading-tight font-semibold whitespace-pre-line">{"SOS\nAssistance"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* CARD 08: Sharing Content */}
        <div className="relative shrink-0 w-[82%] xs:w-[78%] snap-center min-w-0 md:w-auto md:shrink rounded-3xl h-[430px] bg-[#F6F1FE]">
          <div className="relative w-full h-44 sm:h-48 pt-6 px-4 flex justify-center items-end bg-transparent">
            <CardImg src="/img/community-guidelines/SharingContent.svg" alt="Sharing Content" className="max-h-full max-w-full object-contain" />
          </div>
          <div className="p-6 pt-4 flex flex-col flex-grow font-['Poppins']">
            <h3 style={{ color: c.titleColor }} className="font-bold text-sm sm:text-base mb-2 tracking-tight font-['Poppins']">Sharing Content</h3>
            <p style={{ color: c.descColor }} className="text-[13px] leading-relaxed mb-4">
              Share experiences, photos, and tips responsibly. Respect others privacy and rights.
            </p>
            <ul className="flex flex-col mt-auto gap-2.5">
              <li className="flex items-center gap-2.5">
                <CheckIcon />
                <span style={{ color: c.titleColor }} className="text-[13px] font-medium leading-tight">Original Content</span>
              </li>
              <li className="flex items-center gap-2.5">
                <CheckIcon />
                <span style={{ color: c.titleColor }} className="text-[13px] font-medium leading-tight">No Misleading Info</span>
              </li>
              <li className="flex items-center gap-2.5">
                <CheckIcon />
                <span style={{ color: c.titleColor }} className="text-[13px] font-medium leading-tight">Respect Privacy</span>
              </li>
            </ul>
          </div>
        </div>

      </div>

      {/* Bottom Action Bar */}
      <div className="max-w-7xl mx-auto mt-12 bg-[#FFFFFF] border-2 border-gray-100 rounded-3xl px-8 py-8 flex flex-col md:flex-row divide-y md:divide-y-0 md:divide-x divide-gray-200 gap-y-6 font-['Poppins']">
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 flex-1 min-w-0 md:px-8 first:md:pl-0">
          <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0">
            <CardImg src="/img/community-guidelines/ReportUsers.svg" alt="Report Users or Content" className="w-full h-full object-contain" />
          </div>
          <div className="text-center sm:text-left mt-1">
            <p style={{ color: c.titleColor }} className="font-bold text-[13px] mb-1 font-['Poppins']">Report Users or Content</p>
            <p style={{ color: c.descColor }} className="text-[11px] leading-relaxed">Found something suspicious? Report it instantly.</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 flex-1 min-w-0 md:px-8">
          <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0">
            <CardImg src="/img/community-guidelines/BlockUsers.svg" alt="Block Users" className="w-full h-full object-contain" />
          </div>
          <div className="text-center sm:text-left mt-1">
            <p style={{ color: c.titleColor }} className="font-bold text-[13px] mb-1 font-['Poppins']">Block Users</p>
            <p style={{ color: c.descColor }} className="text-[11px] leading-relaxed">Block users anytime if you feel uncomfortable.</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 flex-1 min-w-0 md:px-8 last:md:pr-0">
          <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0">
            <CardImg src="/img/community-guidelines/Enforcement.svg" alt="Enforcement" className="w-full h-full object-contain" />
          </div>
          <div className="text-center sm:text-left mt-1">
            <p style={{ color: c.titleColor }} className="font-bold text-[13px] mb-1 font-['Poppins']">Enforcement</p>
            <p style={{ color: c.descColor }} className="text-[11px] leading-relaxed">Violating our guidelines may result in actions.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunityGuidelinesGrid;